import { Fragment, useEffect, useRef, useState } from "react";
import { ScopeQuoteStrip } from "./ScopeQuoteStrip";
import {
  MoreHorizontal, Copy, Trash2, ArrowUp, ArrowDown, Star, EyeOff, ReceiptText,
  StickyNote, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const money = (v) => {
  const n = Number(v || 0);
  const body = `$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2,
    maximumFractionDigits: 2 })}`;
  return n < 0 ? `-${body}` : body;
};
const num = (v, d = 2) => Number(v || 0).toFixed(d);
const pct = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;

function InlineCell({ value, kind = "num", onCommit, testid, muted = false, width = 72,
                     readOnly = false, placeholder = "" }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const display = () => {
    if (kind === "money") return money(value);
    if (kind === "pct") return pct(value);
    if (kind === "text") return value || placeholder || "—";
    return num(value, kind === "qty" ? 2 : 2);
  };

  if (readOnly) {
    if (kind === "text" && !value && placeholder) return null;
    return <span className="text-slate-600" data-testid={testid}>{display()}</span>;
  }

  if (!editing) {
    return (
      <span
        className={`cursor-pointer decoration-dotted hover:underline ${muted ? "text-slate-400 italic" : ""}`}
        onClick={() => {
          setDraft(kind === "pct" ? String(Number(((value || 0) * 100).toFixed(2)))
            : kind === "text" ? String(value ?? "") : String(Number(value ?? 0)));
          setEditing(true);
        }}
        data-testid={testid}
      >
        {display()}
      </span>
    );
  }

  const commit = async () => {
    setEditing(false);
    if (kind === "text") {
      if (draft !== (value ?? "")) await onCommit(draft);
      return;
    }
    const parsed = parseFloat(draft);
    if (Number.isNaN(parsed)) return;
    const next = kind === "pct" ? parsed / 100 : parsed;
    if (next !== Number(value)) await onCommit(next);
  };

  return (
    <input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === "Tab") commit();
        if (e.key === "Escape") setEditing(false);
      }}
      style={{ width }}
      className={`h-6 rounded border border-[#007BFF] px-1 text-xs ${
        kind === "text" ? "text-left" : "text-right"}`}
      data-testid={`${testid}-input`}
    />
  );
}

const HEADERS = [
  { label: "Description", align: "left" },
  { label: "Qty", align: "right" },
  { label: "UOM", align: "left" },
  { label: "Unit Cost", align: "right" },
  { label: "Mat GM%", align: "right" },
  { label: "Material $", align: "right" },
  { label: "Hrs/Unit", align: "right" },
  { label: "Total Hrs", align: "right" },
  { label: "Lab GM%", align: "right" },
  { label: "Labor $", align: "right" },
  { label: "Tax", align: "right" },
  { label: "Textura", align: "right" },
  { label: "Total Price", align: "right" },
  { label: "Budget Code", align: "left" },
];

// When every scope is collapsed the grid reads as a bid summary, so it drops to the five
// numbers that matter. Opening any scope restores the full working columns.
const COMPACT_HEADERS = [
  { label: "Cost Code", align: "left" },
  { label: "Scope", align: "left" },
  { label: "Total Hrs", align: "right" },
  { label: "GM %", align: "right" },
  { label: "Total Price", align: "right" },
];

export default function LineGrid({
  lines, selected, toggleSelect, toggleSelectAll, onEdit, onDuplicate, onDelete, onProposeLibrary,
  onMove, onQuote, readOnly = false, showLocations = true, showItemNumbers = true,
  quotes = [], onQuoteMode, onQuoteRemove,
}) {
  const ro = { readOnly };
  // Amber matches the "Quote" badge: this cell is why the line is unfinished.
  const flag = (l, value) => (l.incomplete && !Number(value) ? "bg-amber-50" : "");
  const confirmZero = (l, value) => !readOnly && onEdit(l, { cost_confirmed_zero: value });
  const [expanded, setExpanded] = useState([]);
  const [collapsed, setCollapsed] = useState(null);   // null = not initialised yet
  const toggleNote = (id) => setExpanded((e) =>
    e.includes(id) ? e.filter((x) => x !== id) : [...e, id]);
  const toggleSection = (key) => setCollapsed((c) =>
    (c || []).includes(key) ? c.filter((k) => k !== key) : [...(c || []), key]);
  const sections = [];
  lines.forEach((l) => {
    const key = l.section || "";
    let bucket = sections.find((s) => s.key === key);
    if (!bucket) {
      bucket = { key, rows: [] };
      sections.push(bucket);
    }
    bucket.rows.push(l);
  });
  // A scope normally maps to exactly one cost code; show it, or flag a mixed scope.
  // Subtotals exclude excluded lines and alternates, matching the estimate's own totals.
  sections.forEach((sec) => {
    const codes = [...new Set(sec.rows.map((r) => r.cost_code).filter(Boolean))];
    sec.code = codes.length === 1 ? codes[0] : codes.length > 1 ? `${codes.length} codes` : "";
    sec.mixed = codes.length > 1;
    const counted = sec.rows.filter((r) => !r.is_excluded && !r.is_option);
    const sum = (f) => counted.reduce((a, r) => a + Number(r[f] || 0), 0);
    sec.material = sum("total_material");
    sec.hours = sum("total_labor_hours");
    sec.labor = sum("total_labor");
    sec.tax = sum("tax");
    sec.textura = sum("textura");
    sec.total = sum("line_total");
    sec.gm = sum("material_gm_dollars") + sum("labor_gm_dollars");
    sec.gmPct = sec.total ? sec.gm / sec.total : 0;
    sec.excludedCount = sec.rows.length - counted.length;
    sec.real = sec.rows.filter((l) => !l.internal_only);
    sec.internalCount = sec.rows.length - sec.real.length;
  });
  // Scopes read as a bid summary, so the grid opens collapsed.
  const keys = sections.map((sec) => sec.key);
  const keyId = keys.join("|");
  const seenKeys = useRef([]);
  useEffect(() => {
    // A scope added after the grid loaded arrives collapsed too, so the view never flips
    // out of the bid-summary format on its own.
    const fresh = keys.filter((k) => !seenKeys.current.includes(k));
    seenKeys.current = keys;
    if (collapsed === null) setCollapsed(keys);
    else if (fresh.length) setCollapsed((c) => [...(c || []), ...fresh]);
    // eslint-disable-next-line
  }, [keyId]);
  const open = collapsed || [];
  const allCollapsed = keys.length > 0 && keys.every((k) => open.includes(k));

  // Scopes run in cost-code order, not alphabetically; unmapped scopes fall to the bottom.
  sections.sort((a, b) => {
    if (!a.key) return 1;
    if (!b.key) return -1;
    const ac = a.mixed ? "" : a.code, bc = b.mixed ? "" : b.code;
    if (ac && bc && ac !== bc) return ac.localeCompare(bc);
    if (ac && !bc) return -1;
    if (!ac && bc) return 1;
    return a.key.localeCompare(b.key);
  });

  // Selection drives bulk edit and bulk delete, so it only ever covers real scope lines.
  const selectable = lines.filter((l) => !l.internal_only);
  const allSelected = selectable.length > 0 && selectable.every((l) => selected.includes(l.id));
  const compact = allCollapsed;
  const heads = compact ? COMPACT_HEADERS : HEADERS;
  const scopeSelected = (sec) => sec.real.length > 0
    && sec.real.every((l) => selected.includes(l.id));
  // Whole-scope select feeds the existing bulk-edit selection, so no new plumbing is needed.
  const toggleScope = (sec) => {
    const on = !scopeSelected(sec);
    sec.real.forEach((l) => {
      if (selected.includes(l.id) !== on) toggleSelect(l.id);
    });
  };

  return (
    <div className="max-h-[calc(100vh-19rem)] overflow-auto rounded-lg border border-slate-200 bg-white">
      <table className="w-full text-xs" data-testid="estimate-line-table">
        <thead className="sticky top-0 z-20 bg-slate-50 shadow-[inset_0_-1px_0_0_rgb(226,232,240)]">
          <tr>
            <th className="w-14 whitespace-nowrap px-2 py-2">
              <span className="inline-flex items-center gap-1">
                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                       data-testid="line-select-all" />
                {keys.length > 1 && (
                  <button
                    onClick={() => setCollapsed(allCollapsed ? [] : keys)}
                    className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                    title={allCollapsed ? "Expand all scopes" : "Collapse all scopes"}
                    data-testid="toggle-all-sections">
                    {allCollapsed ? <ChevronsUpDown className="h-3.5 w-3.5" />
                                  : <ChevronsDownUp className="h-3.5 w-3.5" />}
                  </button>
                )}
              </span>
            </th>
            {heads.map((h) => (
              <th key={h.label}
                  className={`whitespace-nowrap px-2 py-2 font-medium text-slate-500 ${
                    h.align === "right" ? "text-right" : "text-left"}`}>
                {h.label}
              </th>
            ))}
            {!compact && <th className="w-8" />}
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 && (
            <tr>
              <td colSpan={heads.length + 2} className="px-3 py-12 text-center text-slate-400">
                No lines yet. Use "Add lines" to pull items, groups or a blank line.
              </td>
            </tr>
          )}
          {sections.map((sec) => (
            <Fragment key={`sec-wrap-${sec.key}`}>
              {(
                // top-[33px] parks the scope row directly under the locked column headers
                <tr className="cursor-pointer"
                    onClick={() => toggleSection(sec.key)}
                    data-testid={`line-section-${sec.key || "unassigned"}`}>
                  <td className="sticky top-[33px] z-10 whitespace-nowrap bg-slate-100 px-2 py-1.5 text-slate-400">
                    <span className="inline-flex items-center gap-1">
                      <input type="checkbox" checked={scopeSelected(sec)}
                             onClick={(e) => e.stopPropagation()}
                             onChange={() => toggleScope(sec)}
                             title="Select every line in this scope"
                             data-testid={`section-select-${sec.key}`} />
                      {open.includes(sec.key)
                        ? <ChevronRight className="h-3.5 w-3.5" />
                        : <ChevronDown className="h-3.5 w-3.5" />}
                    </span>
                  </td>
                  {compact && (
                    <td className="sticky top-[33px] z-10 bg-slate-100 px-2 py-1.5">
                      {sec.code && (
                        <span className={`rounded px-1.5 py-0.5 font-mono text-[10px] font-medium ${
                          sec.mixed ? "bg-amber-100 text-amber-700" : "bg-slate-200/80 text-slate-600"}`}
                              data-testid={`section-code-${sec.key}`}>
                          {sec.code}
                        </span>
                      )}
                    </td>
                  )}
                  <td colSpan={compact ? 1 : 5}
                      className="sticky top-[33px] z-10 bg-slate-100 px-2 py-1.5 font-bold text-slate-800">
                    <span className="inline-flex flex-wrap items-baseline gap-x-2">
                      {!compact && sec.code && (
                        <span className={`rounded px-1.5 py-0.5 font-mono text-[10px] font-medium ${
                          sec.mixed ? "bg-amber-100 text-amber-700" : "bg-slate-200/80 text-slate-600"}`}
                              title={sec.mixed ? "This scope has lines on more than one cost code"
                                               : "Cost code"}
                              data-testid={`section-code-${sec.key}`}>
                          {sec.code}
                        </span>
                      )}
                      <span className={sec.key ? "text-[13px] uppercase tracking-wide"
                                               : "italic font-normal text-slate-400"}>
                        {sec.key || "No scope"}
                      </span>
                      <span className="font-normal text-[10px] text-slate-400">
                        {sec.real.length} line{sec.real.length === 1 ? "" : "s"}
                        {sec.internalCount > 0 && ` · ${sec.internalCount} internal`}
                        {sec.excludedCount > 0 && ` · ${sec.excludedCount} not counted`}
                      </span>
                      {!compact && (
                        <span className="font-normal text-[10px] text-slate-500"
                              title="Gross margin on this scope">
                          GM {money(sec.gm)} · {pct(sec.gmPct)}
                        </span>
                      )}
                    </span>
                  </td>
                  {(compact
                    ? [["hours", sec.hours ? num(sec.hours) : "0"],
                       ["gm", pct(sec.gmPct)], ["total", money(sec.total)]]
                    : [["material", money(sec.material)], ["hrs-unit", ""],
                       ["hours", sec.hours ? num(sec.hours) : ""], ["lab-gm", ""],
                       ["labor", money(sec.labor)], ["tax", sec.tax ? money(sec.tax) : ""],
                       ["textura", sec.textura ? money(sec.textura) : ""],
                       ["total", money(sec.total)], ["code", ""]]
                  ).map(([slot, value]) => (
                    <td key={slot}
                        className={`sticky top-[33px] z-10 bg-slate-100 px-2 py-1.5 text-right tabular-nums ${
                          slot === "total" ? "font-semibold text-slate-700" : "text-slate-500"}`}
                        data-testid={value ? `section-${slot}-${sec.key}` : undefined}>
                      {value}
                    </td>
                  ))}
                  {!compact && <td className="sticky top-[33px] z-10 bg-slate-100" />}
                </tr>
              )}
              {quotes.filter((q) => (q.section || "") === sec.key).map((q) => (
                <ScopeQuoteStrip key={q.id} quote={q} colSpan={compact ? 5 : 15} readOnly={readOnly}
                                 onMode={onQuoteMode} onRemove={onQuoteRemove} />
              ))}
              {!open.includes(sec.key) && sec.rows.map((l) => (
                <tr key={l.id}
                    className={`border-t border-slate-100 hover:bg-slate-50/70 ${
                      l.is_excluded ? "opacity-45 line-through" : ""} ${
                      l.internal_only ? "bg-slate-50/60 text-slate-400" : ""}`}
                    data-testid={`line-row-${l.id}`}>
                  <td className="px-2 py-1.5">
                    <input type="checkbox" checked={selected.includes(l.id)}
                           onChange={() => toggleSelect(l.id)}
                           data-testid={`line-checkbox-${l.id}`} />
                  </td>
                  <td className="min-w-[220px] max-w-[320px] px-2 py-1.5 text-slate-800">
                    <div className="flex items-center gap-1.5">
                      <div className="truncate" title={l.description}>
                        <InlineCell {...ro} value={l.description} kind="text" width={280}
                                    testid={`line-desc-${l.id}`}
                                    onCommit={(v) => onEdit(l, { description: v })} />
                      </div>
                      {l.internal_only && (
                        <span className="shrink-0 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-500"
                              title="Internal cost — priced into the total, never itemised on the proposal"
                              data-testid={`line-internal-badge-${l.id}`}>internal</span>
                      )}
                      {l.is_option && (
                        <span className="shrink-0 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700"
                              data-testid={`line-option-badge-${l.id}`}>Alt</span>
                      )}
                      {l.awaiting_quote && (
                        <button onClick={() => onQuote(l)}
                                className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                                data-testid={`line-quote-badge-${l.id}`}>Quote</button>
                      )}
                      {l.line_kind === "vendor_quote" && (
                        <span className="shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                          Quoted
                        </span>
                      )}
                      {l.incomplete && (
                        <button
                          onDoubleClick={() => confirmZero(l, true)}
                          className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                          title="No cost or no margin on this line. Double-click to confirm $0 is intentional."
                          data-testid={`line-incomplete-badge-${l.id}`}>
                          Incomplete
                        </button>
                      )}
                      {l.cost_confirmed_zero && (
                        <button onClick={() => confirmZero(l, false)}
                                className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500"
                                title="$0 confirmed as intentional — click to undo"
                                data-testid={`line-zero-confirmed-${l.id}`}>
                          $0 ok
                        </button>
                      )}
                    </div>
                    {showItemNumbers && (l.item_number || l.manufacturer) && (
                      <div className="mt-0.5 font-mono text-[10px] text-slate-400"
                           data-testid={`line-item-number-${l.id}`}>
                        {[l.manufacturer, l.item_number].filter(Boolean).join(" ")}
                      </div>
                    )}
                    {showLocations && (l.notes || !readOnly) && (
                      <div className="group/note mt-0.5 flex items-start gap-1 text-[10px] leading-snug text-slate-400"
                           data-testid={`line-notes-${l.id}`}>
                        <StickyNote className={`mt-[2px] h-2.5 w-2.5 shrink-0 ${
                          l.notes ? "" : "opacity-0 group-hover/note:opacity-100"}`} />
                        {l.notes && l.notes.length > 46 && (
                          <button onClick={() => toggleNote(l.id)}
                                  className="mt-[1px] shrink-0 text-slate-400 hover:text-[#007BFF]"
                                  title={expanded.includes(l.id) ? "Collapse note" : "Show full note"}
                                  data-testid={`line-notes-toggle-${l.id}`}>
                            {expanded.includes(l.id)
                              ? <ChevronDown className="h-2.5 w-2.5" />
                              : <ChevronRight className="h-2.5 w-2.5" />}
                          </button>
                        )}
                        <div className={`min-w-0 ${l.notes ? "" : "italic opacity-0 group-hover/note:opacity-100"} ${
                          expanded.includes(l.id) ? "whitespace-normal break-words" : "truncate"}`}
                             title={l.notes || ""}>
                          <InlineCell {...ro} value={l.notes} kind="text" width={280}
                                      placeholder="add note" testid={`line-note-${l.id}`}
                                      onCommit={(v) => onEdit(l, { notes: v })} />
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    <InlineCell {...ro} value={l.qty} testid={`line-qty-${l.id}`}
                                onCommit={(v) => onEdit(l, { qty: v })} />
                  </td>
                  <td className="px-2 py-1.5">
                    <InlineCell {...ro} value={l.uom} kind="text" width={56} testid={`line-uom-${l.id}`}
                                onCommit={(v) => onEdit(l, { uom: v })} />
                  </td>
                  <td className={`px-2 py-1.5 text-right tabular-nums ${flag(l, l.effective_unit_cost)}`}
                      onDoubleClick={() => l.incomplete && confirmZero(l, true)}>
                    {l.quote_pricing ? (
                      <div className="flex flex-col items-end leading-tight" data-testid={`line-quote-pricing-${l.id}`}
                           title={l.quote_pricing.label}>
                        {l.quote_pricing.in_use === "vendor_total" ? (
                          <>
                            <span className="text-slate-400" data-testid={`line-library-unit-${l.id}`}>
                              {money(l.quote_pricing.library_unit_cost)}
                              <span className="ml-1 text-[9px] uppercase tracking-wide">not used — vendor total applies</span>
                            </span>
                            <span className="font-medium text-sky-800" data-testid={`line-allocated-unit-${l.id}`}>
                              {money(l.effective_unit_cost)} <span className="text-[9px] font-normal text-slate-400">allocated</span>
                            </span>
                          </>
                        ) : l.quote_pricing.in_use === "quoted_unit" ? (
                          <>
                            <span className="text-slate-400 line-through" data-testid={`line-library-unit-${l.id}`}>
                              {money(l.quote_pricing.library_unit_cost)}
                            </span>
                            <span className="font-medium text-sky-800">{money(l.effective_unit_cost)} <span className="text-[9px] font-normal text-slate-400">quoted</span></span>
                          </>
                        ) : (
                          <InlineCell {...ro} value={l.effective_unit_cost} kind="money"
                                      testid={`line-unit-cost-${l.id}`}
                                      onCommit={(v) => onEdit(l, { unit_cost: v })} />
                        )}
                        <span className="text-[9px] text-sky-700" data-testid={`line-quote-tag-${l.id}`}>
                          {l.quote_pricing.label}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-end">
                        <InlineCell {...ro} value={l.effective_unit_cost} kind="money"
                                    testid={`line-unit-cost-${l.id}`}
                                    onCommit={(v) => onEdit(l, l.cost_source === "vendor_quote"
                                      ? { quoted_unit_cost: v } : { unit_cost: v })} />
                        {l.library_override && (
                          <span className="text-[9px] text-slate-400" data-testid={`line-library-price-${l.id}`}
                                title="Estimate-level override — the cost library keeps its price">
                            library {money(l.library_unit_cost)}
                            {onProposeLibrary && !readOnly && (
                              <button type="button" className="ml-1 underline hover:text-slate-700" data-testid={`line-propose-library-${l.id}`}
                                      onClick={(e) => { e.stopPropagation(); onProposeLibrary(l); }}>propose update</button>)}
                          </span>)}
                        {l.labor_source?.via && l.labor_source.via !== "part" && (
                          <span className={`text-[9px] ${l.labor_source.via === "none" ? "text-red-600" : "text-slate-400"}`} data-testid={`line-labor-source-${l.id}`}
                                title={l.labor_source.labor_item ? `${l.labor_source.labor_item} · ${l.labor_source.hours_per_unit} h/unit (STD LABOR)` : "no labor item resolved"}>
                            labor: {l.labor_source.via === "keyword" ? l.labor_source.labor_item : l.labor_source.via === "scope_default" ? "scope default" : "unresolved"}
                          </span>)}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    <InlineCell {...ro} value={l.material_gm_used} kind="pct"
                                muted={l.material_gm_percent === null || l.material_gm_percent === undefined}
                                testid={`line-mat-gm-${l.id}`}
                                onCommit={(v) => onEdit(l, { material_gm_percent: v })} />
                  </td>
                  <td className={`px-2 py-1.5 text-right tabular-nums text-slate-700 ${flag(l, l.total_material)}`}
                      onDoubleClick={() => l.incomplete && confirmZero(l, true)}
                      data-testid={`line-material-total-${l.id}`}>
                    {money(l.total_material)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    <InlineCell {...ro} value={l.labor_hours_per_unit} testid={`line-hrs-unit-${l.id}`}
                                onCommit={(v) => onEdit(l, { labor_hours_per_unit: v })} />
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-500"
                      data-testid={`line-total-hrs-${l.id}`}>
                    {num(l.total_labor_hours)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    <InlineCell {...ro} value={l.labor_gm_used} kind="pct"
                                muted={l.labor_gm_percent === null || l.labor_gm_percent === undefined}
                                testid={`line-lab-gm-${l.id}`}
                                onCommit={(v) => onEdit(l, { labor_gm_percent: v })} />
                  </td>
                  <td className={`px-2 py-1.5 text-right tabular-nums text-slate-700 ${flag(l, l.total_labor)}`}
                      onDoubleClick={() => l.incomplete && confirmZero(l, true)}
                      data-testid={`line-labor-total-${l.id}`}>
                    {money(l.total_labor)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">
                    <button onClick={() => onEdit(l, { taxable: !l.taxable })}
                            className={l.taxable ? "" : "text-slate-300 line-through"}
                            title={l.taxable ? "Taxable — click to exempt" : "Exempt — click to tax"}
                            data-testid={`line-tax-${l.id}`}>
                      {money(l.tax)}
                    </button>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">
                    {money(l.textura)}
                  </td>
                  <td className="px-2 py-1.5 text-right font-semibold tabular-nums text-slate-900"
                      data-testid={`line-total-${l.id}`}>
                    {money(l.line_total)}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[11px] text-slate-500"
                      data-testid={`line-budget-code-${l.id}`}>
                    {l.budget_code || "—"}
                  </td>
                  <td className={`px-1 ${readOnly ? "invisible" : ""}`}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="rounded p-1 text-slate-500 hover:bg-slate-200"
                                data-testid={`line-actions-${l.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => onMove(l, -1)}
                                          data-testid={`line-move-up-${l.id}`}>
                          <ArrowUp className="mr-2 h-3.5 w-3.5" /> Move up
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onMove(l, 1)}
                                          data-testid={`line-move-down-${l.id}`}>
                          <ArrowDown className="mr-2 h-3.5 w-3.5" /> Move down
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onEdit(l, { is_option: !l.is_option })}
                                          data-testid={`line-toggle-option-${l.id}`}>
                          <Star className="mr-2 h-3.5 w-3.5" />
                          {l.is_option ? "Make base scope" : "Mark as alternate"}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onEdit(l, { is_excluded: !l.is_excluded })}
                                          data-testid={`line-toggle-excluded-${l.id}`}>
                          <EyeOff className="mr-2 h-3.5 w-3.5" />
                          {l.is_excluded ? "Include again" : "Exclude from proposal"}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onQuote(l)}
                                          data-testid={`line-add-quote-${l.id}`}>
                          <ReceiptText className="mr-2 h-3.5 w-3.5" /> Apply vendor quote
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onDuplicate(l)}
                                          data-testid={`line-duplicate-${l.id}`}>
                          <Copy className="mr-2 h-3.5 w-3.5" /> Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onDelete(l)} className="text-red-600"
                                          data-testid={`line-delete-${l.id}`}>
                          <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete line
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {!open.includes(sec.key) && (
                // Breathing room so one scope is obvious from the next when scrolling expanded
                <tr aria-hidden="true" data-testid={`section-spacer-${sec.key || "unassigned"}`}>
                  <td colSpan={heads.length + 2} className="h-4 bg-white p-0" />
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
